import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Divider,
  Grid,
  Modal,
  Typography,
  IconButton,
  Link,
  Card,
  CardContent,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { DropdownSelect } from 'src/App/Dashboards/Market/Components/Inputs';
import SampleListingsTable from './Static/SampleListingsTable';
import { callApi } from 'src/core/callApi';
import { listingsExportPricesUSD } from 'src/utils/stripeConfig';
import { CLOSE_BUTTON_UNHIGHLIGHTED, COLOR_PRIMARY_2, COLOR_TEXT_4 } from 'src/utils/colors';
import { formatMarketName, makeDownloadableBlob } from 'src/utils/functions';
import { COLOR_TOOLTIP_BACKGROUND } from 'src/utils/const';
import { LoadingCard } from 'src/App/Dashboards/Market/Components';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';
import SamplePerformanceTable from './Static/SamplePerformanceTable';
import postOnSlack from 'src/core/postOnSlack';
import { logAmplitudeEvent } from 'src/utils/analytics';

const DataPoint = ({
  label,
  value,
  info,
}: {
  label: string;
  value: string | number;
  info?: JSX.Element | string;
}) => {
  return (
    <Grid
      item
      xs={12}
      container
      direction={'row'}
      justifyContent={'space-between'}
      alignItems={'flex-start'}
    >
      <Typography
        sx={{
          fontWeight: 500,
          color: COLOR_TEXT_4,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {value} {info && <InfoButton tooltipContent={info} />}
      </Typography>
    </Grid>
  );
};

type ListingsExportModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  matches: boolean;
  dataStoreTimePeriodQuery: string | null;
  dataStoreTimeGranularityQuery: string | null;
  isLoggedIn: boolean;
  setLoginModalOpen: (open: boolean) => void;
};

const calculatePriceTier = (numberOfRecords: number, granularity: string) => {
  const monthlyTier1Limit = 1500;
  const monthlyTier2Limit = 4000;
  const yearlyTier1Limit = 3000;
  const yearlyTier2Limit = 7000;

  if (granularity === 'monthly') {
    if (numberOfRecords <= monthlyTier1Limit) return 'tier1';
    else if (numberOfRecords <= monthlyTier2Limit) return 'tier2';
    else return 'tier3';
  } else {
    if (numberOfRecords <= yearlyTier1Limit) return 'tier1';
    else if (numberOfRecords <= yearlyTier2Limit) return 'tier2';
    else return 'tier3';
  }
};

const ListingsExportModal = ({
  open,
  setOpen,
  matches,
  dataStoreTimePeriodQuery,
  dataStoreTimeGranularityQuery,
  isLoggedIn,
  setLoginModalOpen,
}: ListingsExportModalProps) => {
  const search = useSelector(state => (state as any).dashboard.search);
  const user = useSelector(state => (state as any).dashboard.user);

  const marketDisplayName = formatMarketName(search.data);

  const [timePeriodInYears, setTimePeriodInYears] = useState(1);
  const [perfDataGranularity, setPerfDataGranularity] = useState('monthly');
  const [listingLastSeen, setListingLastSeen] = useState(365);
  const [numberOfRecords, setNumberOfRecords] = useState<{ data: null | number; loading: boolean }>(
    { data: null, loading: true }
  );
  const [priceTier, setPriceTier] = useState<{ data: null | string; loading: boolean }>({
    data: null,
    loading: true,
  });
  const [listingsMetadataJSONArray, setListingsMetadataJSONArray] = useState<{
    data: { url: string; added_on: string; last_seen: string }[] | null;
    loading: boolean;
  }>({ data: null, loading: true });
  const [sampleData, setSampleData] = useState<{
    data: any[] | null;
    loading: boolean;
  }>({ data: null, loading: true });
  const [lastUpdated, setLastUpdated] = useState<{
    data: string | null;
    loading: boolean;
  }>({ data: null, loading: true });

  const getNumberOfRecords = async () => {
    setNumberOfRecords({ data: null, loading: true });
    setListingsMetadataJSONArray({ data: null, loading: true });
    const marketID = search.data.id;
    const url = `https://gye077pfi7.execute-api.us-east-2.amazonaws.com/default/getListingsLevelData`;
    const config = {
      queryParams: {
        marketID: marketID,
        timeGranularity: perfDataGranularity,
        lastAvailable: listingLastSeen,
        timePeriodInYears: timePeriodInYears,
        checkNumberOfRecords: 'true',
        url: encodeURIComponent(window.location.href),
      },
    };
    try {
      const resp = await callApi(url, config, true);
      if (resp?.message?.number_of_records) {
        setNumberOfRecords({ data: resp.message.number_of_records, loading: false });
        setListingsMetadataJSONArray({ data: resp.message.listings_metadata, loading: false });
      } else {
        setNumberOfRecords({ data: 10000, loading: false });
      }
    } catch (e) {
      setNumberOfRecords({ data: 10000, loading: false });
    }
  };

  const getSampleData = async () => {
    setSampleData({ data: null, loading: true });
    const marketID = search.data.id;
    const url = `https://gye077pfi7.execute-api.us-east-2.amazonaws.com/default/getListingsLevelData`;

    const config = {
      queryParams: {
        marketID: marketID,
        timeGranularity: perfDataGranularity,
        lastAvailable: listingLastSeen,
        getSampleData: 'true',
        timePeriodInYears: timePeriodInYears,
      },
    };
    const resp = await callApi(url, config, true, false, '', 'GET', true);
    if (!resp) return;

    if (!resp.error) {
      setSampleData({ data: resp.message?.sample_data, loading: false });
      setLastUpdated({ data: resp.message?.last_updated_month, loading: false });
    }
  };

  useEffect(() => {
    if (!dataStoreTimePeriodQuery || !dataStoreTimeGranularityQuery) {
      return;
    }
    ['1', '2', '3'].includes(dataStoreTimePeriodQuery) &&
      setTimePeriodInYears(Number(dataStoreTimePeriodQuery));
    ['monthly', 'yearly'].includes(dataStoreTimeGranularityQuery) &&
      setPerfDataGranularity(dataStoreTimeGranularityQuery);
  }, []);

  useEffect(() => {
    window.history.pushState(
      {},
      '',
      `?listing-export-time-period=${timePeriodInYears}&listing-export-granularity=${perfDataGranularity}`
    );
  }, [timePeriodInYears, perfDataGranularity]);

  useEffect(() => {
    if (!numberOfRecords.loading && numberOfRecords.data) {
      setPriceTier({ data: null, loading: true });
      const calculatedPriceTier = calculatePriceTier(numberOfRecords.data, perfDataGranularity);
      setPriceTier({ data: calculatedPriceTier, loading: false });
    }
  }, [numberOfRecords, perfDataGranularity]);

  useEffect(() => {
    if (search.data.name) {
      getNumberOfRecords();
    }
  }, [search.data, listingLastSeen]);

  useEffect(() => {
    if (search.data.name) {
      getSampleData();
    }
  }, [search.data, listingLastSeen, perfDataGranularity, timePeriodInYears]);

  const PerfDataGranularityOptions = {
    monthly: 'Monthly',
    yearly: 'Yearly',
  };

  const TimePeriodOptions = {
    1: '1 year',
    2: '2 years',
    3: '3 years',
  };

  const ListingLastSeenOptions = {
    30: '30 days',
    180: '180 days',
    365: '1 year',
    999: 'All History',
  };

  const fetchClientSecret = async () => {
    const url =
      'https://2iqbzo7zv7.execute-api.us-east-2.amazonaws.com/default/createStripeCheckoutSession';

    const currentURL = window.location.href;
    const currentBaseURL = currentURL.split('?')[0];

    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        currency: 'usd',
        productName: 'listingsExport',
        price: `${listingsExportPricesUSD[(priceTier as any).data][timePeriodInYears].price}00`,
        successURL: `${currentBaseURL}?listings_export_session_id={CHECKOUT_SESSION_ID}`,
        cancelURL: `${currentBaseURL}?listings_export_payment_failed=true`,
        // isTest: true,
      }),
    });

    if (response.ok) {
      const responseJSON = await response.json();
      const redirectURL = responseJSON.message.redirect_url;
      window.open(redirectURL, '_self');
    } else {
      postOnSlack(
        'tech',
        `Error fetching client secret for Listing Data Store - Stripe Checkout Page: ${user.email}`
      );
      throw new Error(`Error fetching client secret: ${response.statusText}`);
    }
  };

  const storeDownloadConfigInLocalStorage = () => {
    localStorage.setItem(
      'listingsExportConfig',
      JSON.stringify({
        district: search.data.name,
        city: search.data.city,
        countryCode: search.data.countryCode,
        lastAvailable: listingLastSeen,
        timePeriod: timePeriodInYears,
        timeGranularity: perfDataGranularity,
        marketID: search.data.id,
      })
    );
  };

  const handleModalOnClose = () => {
    setOpen(false);
  };

  const DownloadListingsMetadataTooltip = () => (
    <Card>
      <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
        <Grid container direction="column" alignItems="flex-start">
          <Link
            onClick={() =>
              makeDownloadableBlob(
                listingsMetadataJSONArray.data,
                `${
                  search.data && marketDisplayName
                    ? marketDisplayName.replaceAll(' ', '_')
                    : 'exported'
                }_metadata.csv`
              )
            }
          >
            Click to view the listings included in this CSV file.
          </Link>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Modal open={open} onClose={handleModalOnClose}>
      <Grid
        container
        direction="column"
        sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: matches ? '100%' : '90%',
          height: matches ? '100%' : '700px',
          outline: 'none !important',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          pt: 2,
          px: 4,
          pb: 3,
          overflowY: 'scroll',
        }}
      >
        <Grid container>
          <Grid container item justifyContent="flex-end">
            <IconButton
              aria-label="close"
              onClick={handleModalOnClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: CLOSE_BUTTON_UNHIGHLIGHTED,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="h5"
              sx={{ color: COLOR_PRIMARY_2, fontWeight: 500, marginBottom: '10px' }}
            >
              {marketDisplayName} Property-level Data CSV download
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider orientation="horizontal" variant="fullWidth" />
          </Grid>

          <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
            <Grid
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              item
              lg={8}
              md={8}
              xs={12}
            >
              {/* Overview section */}
              <Grid container item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 500, marginBottom: '10px' }}>
                  About Dataset
                </Typography>

                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                  <span style={{ fontWeight: 500, color: COLOR_TEXT_4 }}>Overview</span>
                  <br />
                  This property level dataset provides detailed information for granular analysis,
                  including both listing and performance data.{' '}
                  <a
                    href="https://docs.google.com/spreadsheets/d/1rdoDmy7NzBLn7xiuzxg9DLB9ze5-OmNua8QJsZ4Yz6g/edit?pli=1&gid=0#gid=0"
                    target="_blank"
                  >
                    See data dictionary
                  </a>
                  .
                </Typography>

                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                  <span style={{ fontWeight: 500, color: COLOR_TEXT_4 }}>Listing Data</span>
                  <br />
                  Contains comprehensive information about each listing. Last Updated on{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {lastUpdated.data ? lastUpdated.data : '...'}
                  </span>
                </Typography>

                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                  <span style={{ fontWeight: 500, color: COLOR_TEXT_4 }}>Performance Data</span>
                  <br />
                  Includes key metrics such as occupancy rate, daily rate, and revenue. Last Updated
                  on{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {lastUpdated.data ? lastUpdated.data : '...'}
                  </span>
                  <br />
                  <span style={{ fontStyle: 'italic' }}>
                    <span style={{ fontWeight: 500 }}>Note:</span> Only listings available for more
                    than 180 days in the past year are included.
                  </span>
                </Typography>

                <Typography variant="body1" sx={{ marginBottom: '20px' }}>
                  <span style={{ fontWeight: 500, color: COLOR_TEXT_4 }}>
                    Options for Performance Data
                  </span>
                  <br />- <span style={{ fontWeight: 500 }}>Time Period:</span> Select the number of
                  years for which you want to view performance data.
                  <br />- <span style={{ fontWeight: 500 }}>Time Granularity:</span> Choose the
                  level of detail for performance data. For example, selecting 'monthly' will
                  display data by month (e.g., January 2024, February 2024, etc.).
                </Typography>
              </Grid>

              {/* Sample Data Section */}
              <Grid container direction="column" item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 500, marginBottom: '20px' }}>
                  Data Sample Preview
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: COLOR_TEXT_4,
                    marginBottom: '10px',
                  }}
                >
                  Listing
                </Typography>
                {sampleData.loading ? (
                  <LoadingCard height={200} type="bars" />
                ) : (
                  <SampleListingsTable data={sampleData.data} marketName={marketDisplayName} />
                )}

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: COLOR_TEXT_4,
                    marginTop: '10px',
                    marginBottom: '10px',
                  }}
                >
                  Performance
                </Typography>
                {sampleData.loading ? (
                  <LoadingCard height={200} type="bars" />
                ) : (
                  <SamplePerformanceTable
                    data={sampleData.data}
                    granularity={perfDataGranularity}
                    marketName={marketDisplayName}
                  />
                )}
              </Grid>
            </Grid>

            <Divider orientation="vertical" variant="fullWidth" flexItem />

            {/* Details section */}
            <Grid
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              item
              lg={3}
              md={3}
              xs={12}
            >
              <Typography variant="h6" sx={{ fontWeight: 500, marginBottom: '20px' }}>
                Record Summary and Pricing
              </Typography>

              {/* Filter Section */}
              <Grid
                container
                item
                xs={12}
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ marginBottom: 2 }}
              >
                <DropdownSelect
                  name="Time Period"
                  value={timePeriodInYears}
                  setValue={setTimePeriodInYears}
                  options={TimePeriodOptions}
                />
                <DropdownSelect
                  name="Time Granularity"
                  value={perfDataGranularity}
                  setValue={setPerfDataGranularity}
                  options={PerfDataGranularityOptions}
                />
                {/* <DropdownSelect
                name="Last Availability"
                value={listingLastSeen}
                setValue={setListingLastSeen}
                options={ListingLastSeenOptions}
              /> */}
              </Grid>

              {numberOfRecords.loading || !numberOfRecords.data || !priceTier.data ? (
                <Grid container item xs={12}>
                  <LoadingCard height={150} type="bars" />
                </Grid>
              ) : (
                <Grid container item xs={12}>
                  <DataPoint
                    label="Number of Records"
                    value={numberOfRecords.data == 10000 ? '10000+' : numberOfRecords.data}
                    info={
                      listingsMetadataJSONArray.data ? (
                        <DownloadListingsMetadataTooltip />
                      ) : (
                        undefined
                      )
                    }
                  />
                  <DataPoint
                    label="Total Price"
                    value={`$${listingsExportPricesUSD[priceTier.data][timePeriodInYears].price}`}
                  />
                </Grid>
              )}

              {!priceTier.loading && priceTier.data && (
                <>
                  {isLoggedIn ? (
                    <Button
                      onClick={() => {
                        logAmplitudeEvent('listing_export_purchase_attempted', {
                          timePeriod: timePeriodInYears,
                          timeGranularity: perfDataGranularity,
                          marketID: search.data.id,
                        });
                        fetchClientSecret();
                        storeDownloadConfigInLocalStorage();
                      }}
                      variant="contained"
                      sx={{ marginTop: 2, color: 'white', backgroundColor: COLOR_PRIMARY_2 }}
                      fullWidth
                    >
                      <DownloadIcon sx={{ mr: 0.5 }} /> Download as CSV
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        setOpen(false);
                        setLoginModalOpen(true);
                      }}
                      variant="contained"
                      sx={{ marginTop: 2, color: 'white', backgroundColor: COLOR_PRIMARY_2 }}
                      fullWidth
                    >
                      <LockOutlinedIcon sx={{ mr: 0.5 }} /> Sign up to download the CSV
                    </Button>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default ListingsExportModal;
